import { Ban, CheckCheck } from "lucide-react";
import { cn } from "@/lib/utils";
import { formatTime } from "@/lib/datetime";
import { ChatCustomerActionChips } from "./ChatCustomerActionChips";
import { CHAT_BUBBLE_MAX_WIDTH_CLASS } from "./theme";
import type { ChatMessage } from "./types";

export interface MessengerChatBubbleProps {
  message: ChatMessage;
  className?: string;
  blockedLabel?: string;
}

export function MessengerChatBubble({
  message,
  className,
  blockedLabel = "Message blocked",
}: MessengerChatBubbleProps) {
  const timeLabel = formatTime(message.createdAt);
  const isBlocked = message.status === "blocked";
  const actions = message.customerActions ?? [];

  if (message.role === "system") {
    return (
      <div className={cn("flex justify-center px-2 py-1", className)}>
        <div className="max-w-[92%] rounded-lg bg-surface-0 px-3 py-2 text-center shadow-xs ring-1 ring-surface-200">
          <p className="whitespace-pre-wrap text-[13px] leading-snug text-surface-600">
            {message.content}
          </p>
          <p className="mt-1 text-[10px] text-surface-400">{timeLabel}</p>
        </div>
      </div>
    );
  }

  if (message.role === "notice") {
    return (
      <div className={cn("flex justify-center px-2 py-1", className)}>
        <div className="max-w-[92%] rounded-lg border border-dashed border-warning-300 bg-warning-50 px-3.5 py-2.5 text-center shadow-xs">
          {message.senderLabel ? (
            <p className="mb-1 text-[11px] font-semibold text-warning-700">
              {message.senderLabel}
            </p>
          ) : null}
          <p className="whitespace-pre-wrap text-[13px] leading-snug text-warning-900">
            {message.content}
          </p>
          <ChatCustomerActionChips actions={actions} tone="notice" />
          <p className="mt-1 text-[10px] text-warning-600">{timeLabel}</p>
        </div>
      </div>
    );
  }

  const isOwn = message.role === "self";

  return (
    <div
      className={cn(
        "flex min-w-0 max-w-full flex-col px-2 py-0.5",
        isOwn ? "items-end" : "items-start",
        className,
      )}
    >
      <div
        className={cn(
          "flex w-full min-w-0",
          isOwn ? "justify-end" : "justify-start",
        )}
      >
        <div
          className={cn(
            "relative w-fit shrink-0 px-3.5 py-2 shadow-xs",
            CHAT_BUBBLE_MAX_WIDTH_CLASS,
            isBlocked
              ? "rounded-2xl rounded-tr-md bg-error-50 text-error-900 ring-1 ring-inset ring-error-200"
              : isOwn
                ? "rounded-2xl rounded-br-md bg-primary-50 text-surface-900 ring-1 ring-inset ring-primary-600/15"
                : "rounded-2xl rounded-bl-md bg-surface-0 text-surface-900 ring-1 ring-inset ring-surface-200",
          )}
        >
          {!isOwn && message.senderLabel ? (
            <p className="mb-0.5 break-words text-[11px] font-semibold text-primary-700">
              {message.senderLabel}
            </p>
          ) : null}
          {isBlocked ? (
            <p className="mb-1 flex items-center gap-1 text-[11px] font-medium text-error-600">
              <Ban className="h-3 w-3 shrink-0" />
              {blockedLabel}
            </p>
          ) : null}
          <p className="break-words whitespace-pre-wrap text-sm leading-relaxed">
            {message.content}
          </p>
          {isBlocked && message.blockReason ? (
            <p className="mt-1 text-[11px] italic text-error-600">
              {message.blockReason}
            </p>
          ) : null}
          <div
            className={cn(
              "mt-1 flex items-center justify-end gap-1",
              "text-surface-400",
            )}
          >
            <span className="text-[10px] tabular-nums">{timeLabel}</span>
            {isOwn && !isBlocked && message.showReadReceipt !== false ? (
              <CheckCheck
                className="h-4 w-4 shrink-0 text-primary-600/70"
                aria-hidden
              />
            ) : null}
          </div>
        </div>
      </div>
      {message.footer}
    </div>
  );
}
